import { useQuery } from "@tanstack/react-query";
import { customRequest, queryParamsStringify } from "./DataProvider.utils";
import { FIVE_MINUTE_STALE_TIME } from "../constants/DataProvider.constants";

interface IFetchDataRequestProps<TResponse, TFormattedResponse = TResponse> {
  endpoint: string;
  baseURL?: string;
  params?: Record<string, any>;
  isExternal?: boolean; // specify to use internal api client for api request
  queryKey?: (string | number | object)[]; // unique key for the query
  skipAccessToken?: boolean;
  formatResponse?: (response: TResponse) => TFormattedResponse; // allows to customize how the API response is transformed before it’s returned
}

const useFetchDataRequest = <TResponse, TFormattedResponse = TResponse>({
  baseURL,
  endpoint,
  params,
  isExternal = false,
  queryKey,
  skipAccessToken,
  formatResponse,
}: IFetchDataRequestProps<TResponse, TFormattedResponse>) => {
  const fetchData = () =>
    customRequest<TResponse>({
      baseURL,
      endpoint,
      isExternal,
      payload: params,
      skipAccessToken,
    }).then((response) => response.data);

  const apiKey = queryKey || [baseURL, endpoint, params].filter(Boolean);
  if (params) {
    apiKey.push(queryParamsStringify(params));
  }

  return useQuery({
    // use custom queryKey, if provided
    // else, use auto generated queryKey (combination of api endpoint and it's params)
    queryKey: apiKey,
    staleTime: FIVE_MINUTE_STALE_TIME,

    queryFn: fetchData,
    select: formatResponse,
  });
};

export default useFetchDataRequest;
